import { useEffect, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import { Trash2, Users, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function SharingPage() {
  const { activeDog } = useDogs();
  const [shares, setShares] = useState([]);
  const [received, setReceived] = useState([]);
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState("view");
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadOutgoing = async () => {
    if (!activeDog || activeDog.shared) { setShares([]); return; }
    try {
      const { data } = await api.get("/shares", { params: { dog_id: activeDog.id } });
      setShares(data.items || []);
    } catch (e) {
      setShares([]);
    }
  };

  const loadIncoming = async () => {
    try {
      const { data } = await api.get("/shares/received");
      setReceived(data.items || []);
    } catch (e) {
      setReceived([]);
    }
  };

  useEffect(() => {
    loadOutgoing();
    api.get("/reminders/status").then(({ data }) => setEmailEnabled(data.enabled)).catch(() => {});
  }, [activeDog]);

  useEffect(() => { loadIncoming(); }, []);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  const isShared = activeDog.shared;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/shares", { dog_id: activeDog.id, email, permission });
      setEmail("");
      toast.success("Shared with " + email);
      loadOutgoing();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share?")) return;
    await api.delete(`/shares/${id}`);
    loadOutgoing();
  };

  const sendTestEmail = async () => {
    setTesting(true);
    try {
      await api.post("/reminders/send-test", {
        subject: `Your Dog Days reminder: ${activeDog.name}`,
        html: `<div style="font-family:sans-serif;padding:20px;"><h2>Reminder</h2><p>It's time to line brush <strong>${activeDog.name}</strong>!</p></div>`,
      });
      toast.success("Test email sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="sharing-page">
      {/* Outgoing — who has access to my active dog */}
      <div>
        <SectionTitle overline="Collaboration" title="Sharing" />
        {isShared ? (
          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-8">
              <p className="text-sm text-muted-foreground">
                This dog is shared with you by another owner. Only the owner can manage sharing.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
              <CardContent className="p-0">
                {shares.length === 0 ? (
                  <div className="p-10 text-center">
                    <Users size={20} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">No one else has access to {activeDog.name} yet.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {shares.map((s) => (
                      <li key={s.id} className="px-6 py-4 flex items-center justify-between" data-testid={`share-row-${s.id}`}>
                        <div>
                          <p className="font-medium">{s.shared_with_email}</p>
                          <p className="text-xs text-muted-foreground capitalize">{s.permission}</p>
                        </div>
                        <button onClick={() => revoke(s.id)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" data-testid={`revoke-share-${s.id}`} aria-label="Revoke share">
                          <Trash2 size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-6">
                <p className="font-medium mb-4">Invite by email</p>
                <form onSubmit={submit} className="space-y-3" data-testid="invite-share-form">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="share-email-input" />
                    <p className="text-xs text-muted-foreground mt-1">User must already have an account.</p>
                  </div>
                  <div>
                    <Label>Permission</Label>
                    <select value={permission} onChange={(e) => setPermission(e.target.value)} className="mt-1.5 w-full border border-border rounded-md bg-card px-3 py-2 text-sm" data-testid="share-permission-select">
                      <option value="view">View only</option>
                      <option value="edit">Can edit</option>
                    </select>
                  </div>
                  <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="submit-share-button">Share</Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Incoming — dogs shared WITH me */}
      <div data-testid="received-shares-section">
        <SectionTitle overline="Inbox" title="Shared with me" />
        {received.length === 0 ? (
          <Card className="border-dashed border-border shadow-none rounded-lg">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No dogs have been shared with you yet. When someone shares a dog, it will appear here and in your dog switcher.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="received-shares-list">
            {received.map((r) => (
              <Card key={r.id} className="border-border shadow-none rounded-lg" data-testid={`received-share-${r.id}`}>
                <CardContent className="p-5 flex items-start gap-4">
                  {r.dog?.photo_url ? (
                    <img src={r.dog.photo_url} alt={r.dog?.name || "Dog"} className="h-14 w-14 rounded-md object-cover border border-border shrink-0" />
                  ) : (
                    <div className="h-14 w-14 rounded-md bg-secondary border border-border flex items-center justify-center shrink-0">
                      <Users size={20} className="text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate" data-testid={`received-dog-name-${r.id}`}>{r.dog?.name || "Unknown dog"}</p>
                    {r.dog?.breed && <p className="text-xs text-muted-foreground truncate">{r.dog.breed}</p>}
                    <p className="text-xs text-muted-foreground mt-2 truncate" data-testid={`received-share-owner-${r.id}`}>
                      Shared by <span className="font-medium text-foreground">{r.owner?.name || r.owner?.email || "—"}</span>
                    </p>
                    {r.owner?.email && r.owner?.name && (
                      <p className="text-[11px] text-muted-foreground truncate">{r.owner.email}</p>
                    )}
                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide bg-secondary text-secondary-foreground rounded px-2 py-0.5">
                      {r.permission === "edit" ? <Pencil size={11} /> : <Eye size={11} />}
                      {r.permission === "edit" ? "Can edit" : "View only"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <SectionTitle overline="Reminders" title="Email reminders" />
        <Card className="border-border shadow-none rounded-lg">
          <CardContent className="p-6 sm:p-8">
            <p className="text-sm text-foreground/80 mb-4">
              {emailEnabled
                ? "Email reminders are configured. Send yourself a test below."
                : "Email reminders are turned off. Add a Resend API key in backend .env (RESEND_API_KEY) to enable, then restart the backend."}
            </p>
            <Button onClick={sendTestEmail} disabled={!emailEnabled || testing} className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="send-test-email-button">
              {testing ? "Sending…" : "Send test reminder to me"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
